import { SegmentConditionType, SegmentFormPageSegmentConditionRecursiveFragment } from "@/graphql";
import { generateRandomId } from "@/utils";

export interface Condition extends SegmentFormPageSegmentConditionRecursiveFragment {
  rowId: string;
  conditions?: Condition[];
}

/**
 * Fill every conditions with a randomly generated row id
 */
export function fillRowIds(conditions?: SegmentFormPageSegmentConditionRecursiveFragment[] | null): Condition[] {
  if (!conditions || !Array.isArray(conditions) || !conditions.length) {
    return [];
  }
  return conditions.map((condition) => ({
    ...condition,
    rowId: generateRandomId(),
    conditions: fillRowIds(condition.conditions),
  }));
}

/**
 * Returns true if the condition and its sub-conditions are only of the AND or TEST type
 */
const hasOnlyANDConditions = (condition: Condition): boolean => {
  if (condition.type === SegmentConditionType.Test) {
    return true;
  }

  if (condition.type === SegmentConditionType.Or) {
    return false;
  }

  return !(condition.conditions || []).map(hasOnlyANDConditions).includes(false);
};

/**
 * Returns all the TEST conditions from the sub-conditions of the condition
 */
const extractTestCondition = (condition: Condition): Condition[] => {
  if (condition.type === SegmentConditionType.Test) {
    return [condition];
  }

  if (condition.type === SegmentConditionType.Or) {
    throw new Error("ooooo");
  }

  return (condition.conditions || []).flatMap(extractTestCondition);
};

/**
 * Recursively clean the conditions tree using the boolean logic starting by a condition.
 */
const sanitizeCondition = (condition: Condition): Condition | null => {
  if (condition.type === SegmentConditionType.Test) {
    return condition;
  }

  if (condition.conditions?.length === 0) {
    return null;
  }

  if (condition.conditions?.length === 1) {
    return condition.conditions[0];
  }

  if (condition.type === SegmentConditionType.And && hasOnlyANDConditions(condition)) {
    return {
      ...condition,
      conditions: extractTestCondition(condition),
    };
  }

  const conditions = condition.conditions?.map(sanitizeCondition).filter((o) => o != null) as Condition[];

  return {
    ...condition,
    conditions,
  };
};

const _sanitizeConditions = (conditions: Condition[]): Condition[] => {
  const values = conditions.map(sanitizeCondition).filter((o) => o != null) as Condition[];
  return values.length === 1 && [SegmentConditionType.And, SegmentConditionType.Or].includes(values[0].type)
    ? values
    : [
        {
          type: SegmentConditionType.And,
          rowId: generateRandomId(),
          conditions: values,
        },
      ];
};

/**
 * Recursively clean the conditions tree using the boolean logic.
 */
export const sanitizeConditions = (conditions: Condition[]): Condition[] =>
  _sanitizeConditions(_sanitizeConditions(conditions));

export interface ConditionError {
  featureId?: string | null;
  operator?: string | null;
  testValue?: string | null;
  rowId: string;
}

/**
 * Returns the rowId of a condition that doesn't fill the featureId / operator / testValue
 */

const _isConditionInError = (condition: Condition): ConditionError[] => {
  const error: ConditionError = { rowId: condition.rowId };
  let hasError = false;
  if (condition.type === SegmentConditionType.Test) {
    if (!condition.featureId) {
      error.featureId = "the featureId is required";
      hasError = true;
    }
    if (!condition.operator) {
      error.operator = "the operator is required";
      hasError = true;
    }
    if (condition.testValue == null || condition.testValue === "") {
      error.testValue = "the value is required";
      hasError = true;
    }
    return hasError ? [error] : [];
  }

  return (condition.conditions || []).flatMap(_isConditionInError).filter((o) => o != null);
};

export const getConditionsInError = (conditions: Condition[]): ConditionError[] =>
  conditions.flatMap(_isConditionInError);
